import { Injectable } from '@angular/core';
import { CommonDataService } from '../../common-data.service';
import { CONSTANTS } from '../../../Constants/Constants';
import { ReportPageOrientation } from '../../../models/reports/ReportPageOrientation';
import { ReportList } from '../../../models/dtos/Enums/ReportList';
import { IReportData } from '../../../models/reports/IReportData';
import { TranslateService } from '@ngx-translate/core';
import { ITableData } from '../../../models/reports/ITableData';
import { SQWebArea, SQWebSystem, MiscData, Miscellaneous, Subcontractors, SQWebProject, MiscLevelwiseData, TypicalOfSystemHelper } from '../../../models/dtos';
import { IDataItem } from '../../../models/reports/IDataItem';
import { MultilingualCurrencyPipe } from '../../../pipes/multilingual-currency-pipe/multilingual-currency.pipe';
import { IndexDBService } from '../../common/indexDB.service';
import { ISubsMiscSum } from '../../../models/reports/ISubsMiscSum';
import { SubsMiscRow } from '../../../models/reports/SubsMiscRow';
import { SubsMiscWithNotesRow } from '../../../models/reports/SubsMiscWithNotesRow';
import { ReportsCommonService } from '../reports-common/reports-common.service';
import { SQWebAreaChildren } from '../../../models/SQWebAreaChildren';

@Injectable()
export class SolSubContractorMiscellaneousService {

  private miscLevelwiseData: MiscLevelwiseData;
  private sqWebProject: SQWebProject;
  private subContractorMiscInfoList: ITableData[] = [];
  private subContractorMiscWithNotesInfoList: ITableData[] = [];
  private reportGenerated: boolean = false;
  private processedTypicalSystemIdsPerArea: Map<string, Set<string>> = new Map();

  constructor(
    private readonly commonDataService: CommonDataService,
    private readonly reportsCommonService: ReportsCommonService,
    private readonly translate: TranslateService,
    private readonly multilingualCurrencyPipe: MultilingualCurrencyPipe,
    private readonly indexDbService: IndexDBService
  ) { }

  generateReport(reportId: ReportList, pageOrientation: ReportPageOrientation = CONSTANTS.REPORT_PAGEORIENTATION_PORTRAIT, internalReport: boolean = true): void {
      this.sqWebProject = this.commonDataService.getSQWebProject();
      this.indexDbService.getAllMiscData().then((projectMiscData) => {
        this.miscLevelwiseData = projectMiscData;
        const reportName = this.reportsCommonService.getSelectedReportName();
        const reportHeaderData = this.commonDataService.getReportHeaderData();
        let reportData: IReportData = {
          selectionName: this.translate.instant(reportName),
          reportTitle: this.translate.instant(reportName),
          reportTitles: {
            data: this.reportsCommonService.generateReportTitles(reportHeaderData)
          },
          tableData: this.generateTableData(reportId),
          pageOrientation: pageOrientation,
          internalReport: internalReport
        }
        this.reportsCommonService.setSelectedReport(reportData);
      });
    }
  
    generateTableData(reportId: ReportList): ITableData[] {
      // TODO: Clear reportGenerated on Open and Close of Estimate
      if(!this.reportGenerated){
        this.generateSubContractorsAndMiscellaneousReports();
        this.reportGenerated = true;
      }
  
      switch (reportId) {
        case ReportList.SubcontrctorAndMiscCostViewModel:
          return this.subContractorMiscInfoList;
        case ReportList.SubContractorMiscNotesViewModel:
          return this.subContractorMiscWithNotesInfoList;
        default:
          return [];
      }
      
    }

    generateSubContractorsAndMiscellaneousReports(): void {
      this.processedTypicalSystemIdsPerArea.clear();
      this.processAllLevels();
    }

    /**
     * Determines if a typical system should be skipped within a given area.
     * Only the first occurrence of a typical system per area is processed.
     */
    private shouldSkipSystem(systemItem: SQWebSystem, areaId: string): boolean {
      const typicalId = systemItem.systemTypicalOfHelperUniqueId;
      if (!typicalId) {
        return false;
      }
      if (!this.processedTypicalSystemIdsPerArea.has(areaId)) {
        this.processedTypicalSystemIdsPerArea.set(areaId, new Set());
      }
      const areaProcessedSet = this.processedTypicalSystemIdsPerArea.get(areaId)!;
      if (areaProcessedSet.has(typicalId)) {
        return true;
      }
      areaProcessedSet.add(typicalId);
      return false;
    }

    private processAllLevels(): void {
      // Process project level for all reports in single pass
      this.getProjectLevelSubsAndMiscCostsAllReports();

      // Process all areas
      for (const area of this.sqWebProject.areas) {
        if (!area?.isItemVisible) {
          continue;
        }
        this.processAreaLevelAllReports(area);
      }
    }

    private addMiscFeeRow(
      rows: SubsMiscRow,
      sum: ISubsMiscSum,
      cost: number,
      descriptionKey: string,
      checkPPMMode: boolean = false
    ): void {

      const shouldAdd = checkPPMMode 
        ? (!this.commonDataService.globalPPMmode && cost !== CONSTANTS.ZERO)
        : (cost !== CONSTANTS.ZERO);

      if (shouldAdd) {
        const baseRow = {
          referenceNumber: CONSTANTS.EMPTY_STRING,
          description: this.translate.instant(descriptionKey),
          subCost: CONSTANTS.ZERO,
          miscCost: cost,
          isSystemHeader: false
        };
        
        rows.push(baseRow);
        
        sum.miscellaneousCost += cost;
      }
    }

    private collectSubsAndMiscRows( miscData: MiscData, sum: ISubsMiscSum ): SubsMiscRow {
      const rows: SubsMiscRow = [];

      if (!miscData) {
        return rows;
      }

      // subcontractors
      if (miscData.subcontractorCollection) {
        miscData.subcontractorCollection.forEach((sub: Subcontractors) => {
          if (sub.cost > CONSTANTS.ZERO) {
            rows.push({
              referenceNumber: sub.referenceNumber,
              description: sub.description,
              subCost: sub.cost,
              miscCost: CONSTANTS.ZERO,
              isSystemHeader: false
            });
            sum.subcontractorCost += sub.cost;
          }
        });
      }

      // miscellaneous
      if (miscData.miscellaneousCollection) {
        miscData.miscellaneousCollection.forEach((misc: Miscellaneous) => {
          if (misc.cost > CONSTANTS.ZERO) {
            rows.push({
              referenceNumber: misc.referenceNumber,
              description: misc.description,
              subCost: CONSTANTS.ZERO,
              miscCost: misc.cost,
              isSystemHeader: false
            });
            sum.miscellaneousCost += misc.cost;
          }
        });
      }

      return rows;
    }

    private collectSubsAndMiscRowsWithNotes(
      miscData: MiscData,
      sum: ISubsMiscSum
    ): SubsMiscWithNotesRow {
      const rows: SubsMiscWithNotesRow = [];

      if (!miscData) {
        return rows;
      }

      // subcontractors
      if (miscData.subcontractorCollection) {
        miscData.subcontractorCollection.forEach((sub: Subcontractors) => {
          if (sub.cost > CONSTANTS.ZERO) {
            rows.push({
              referenceNumber: sub.referenceNumber,
              description: sub.description,
              subCost: sub.cost,
              miscCost: CONSTANTS.ZERO,
              notes: sub.notes || '',
              isSystemHeader: false
            });
            sum.subcontractorCost += sub.cost;
          }
        });
      }

      // miscellaneous
      if (miscData.miscellaneousCollection) {
        miscData.miscellaneousCollection.forEach((misc: Miscellaneous) => {
          if (misc.cost > CONSTANTS.ZERO) {
            rows.push({
              referenceNumber: misc.referenceNumber,
              description: misc.description,
              subCost: CONSTANTS.ZERO,
              miscCost: misc.cost,
              notes: misc.notes || '',
              isSystemHeader: false
            });
            sum.miscellaneousCost += misc.cost;
          }
        });
      }

      return rows;
    }

    private addDataRowsToTable(
      tableIndex: number,
      rows: SubsMiscRow
    ): void {
      let isInsideSystem = false;

      rows.forEach(row => {
        if (row.isSystemHeader) {
          // System header row - only show in Reference # column with blue background
          this.subContractorMiscInfoList[tableIndex].data.push({
            data: [
              { data: row.referenceNumber, isSubHeader: true, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "left", textColor: CONSTANTS.REPORT_DARK_BLUE_COLOR },
              { data: row.typicalOf || '', isSubHeader: true, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
              { data: '', isSubHeader: true, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
              { data: '', isSubHeader: true, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" }
            ]
          });
          isInsideSystem = true;
        } else {
          // Regular data row - hierarchyLevel 1 for system items, 0 for area items
          const hierarchyLevel = isInsideSystem ? CONSTANTS.ONE : CONSTANTS.ZERO;
          this.subContractorMiscInfoList[tableIndex].data.push({
            data: [
              { data: row.referenceNumber + CONSTANTS.SPACE + row.description, isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: hierarchyLevel, alignment: "left" },
              { data: '', isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
              { data: row.subCost > CONSTANTS.ZERO ? this.formatCurrency(row.subCost)  : '', isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "right" },
              { data: row.miscCost > CONSTANTS.ZERO ? this.formatCurrency(row.miscCost) : '', isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "right" }
            ]
          });
        }
      });
    }

    private addDataRowsToTableWithNotes(tableIndex: number, rows: SubsMiscWithNotesRow): void {
      let isInsideSystem = false;
      rows.forEach(row => {
        if (row.isSystemHeader) {
          this.subContractorMiscWithNotesInfoList[tableIndex].data.push({
            data: [
              { data: '', isSubHeader: true, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "left" },
              { data: row.description, isSubHeader: true, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "left", textColor: CONSTANTS.REPORT_DARK_BLUE_COLOR },
              { data: row.typicalOf || '', isSubHeader: true, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
              { data: '', isSubHeader: true, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
              { data: '', isSubHeader: true, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
              { data: '', isSubHeader: true, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" }
            ]
          });
          isInsideSystem = true;
        } else {
          const hierarchyLevel = isInsideSystem ? CONSTANTS.ONE : CONSTANTS.ZERO;
          this.subContractorMiscWithNotesInfoList[tableIndex].data.push({
            data: [
              { data: row.referenceNumber, isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "left" },
              { data: row.description, isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: hierarchyLevel, alignment: "left" },
              { data: '', isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
              { data: row.subCost > CONSTANTS.ZERO ? this.formatCurrency(row.subCost) : '', isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "right" },
              { data: row.miscCost > CONSTANTS.ZERO ? this.formatCurrency(row.miscCost) : '', isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "right" },
              { data: row.notes, isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "left" }
            ]
          });
        }
      });
    }

    private getProjectLevelSubsAndMiscCostsAllReports(): void {
      const projectUniqueId: string = this.sqWebProject.itemUniqueId;
      const projectMiscData: MiscData = this.miscLevelwiseData.miscsCollection[projectUniqueId];
      
      // Separate sums for each report
      const sum1: ISubsMiscSum = { subcontractorCost: CONSTANTS.ZERO, miscellaneousCost: CONSTANTS.ZERO };
      const sum2: ISubsMiscSum = { subcontractorCost: CONSTANTS.ZERO, miscellaneousCost: CONSTANTS.ZERO };

      // Collect rows for all reports
      const rows1 = this.collectSubsAndMiscRows(projectMiscData, sum1);
      const rows2 = this.collectSubsAndMiscRowsWithNotes(projectMiscData, sum2);

      // Add project-level fees only to Report 1 (SUBCONTRACTOR_AND_MISCELLANEOUS_COSTS)
      const estimateTab = this.commonDataService.getEstimateMetaData().estimateTab;;
      this.addMiscFeeRow(rows1, sum1, estimateTab.autoRiskCost, 'AUTO_RISK', true); // Auto Risk checks PPM mode
      this.addMiscFeeRow(rows1, sum1, estimateTab.technologyFeeCost, 'TECHNOLOGY_FEE', true); // Technology Fee checks PPM mode
      this.addMiscFeeRow(rows1, sum1, estimateTab.mechFeeCost, 'MECH_FEE');
      this.addMiscFeeRow(rows1, sum1, estimateTab.subcontractorFeeCost, 'SUB_CONTRACTOR_FEE');

      // Create table for Subs and Mics Report
      if (rows1.length) {
        this.generateSubsMiscTable('PROJECT_LEVEL');
        const tableIndex = this.subContractorMiscInfoList.length - CONSTANTS.ONE;
        this.addDataRowsToTable(tableIndex, rows1);
        this.addSubsMiscFooter(tableIndex, sum1);
      }

      // Create table for Subs and Mics with Notes Report
      if (rows2.length) {
        this.generateSubsMiscTableWithNotes('PROJECT_LEVEL');
        const tableIndex = this.subContractorMiscWithNotesInfoList.length - CONSTANTS.ONE;
        this.addDataRowsToTableWithNotes(tableIndex, rows2);
        this.addSubsMiscFooterWithNotes(tableIndex, sum2);
      }
    }

    // Process area level for all reports
    private processAreaLevelAllReports(area: SQWebArea): void {
      if (!area?.isItemVisible) {
        return;
      }

      const areaUniqueId = area.itemUniqueId;
      const areaMiscData = this.miscLevelwiseData.miscsCollection[areaUniqueId];

      // Separate rows and sums for each report
      const subsMiscRows: SubsMiscRow = [];
      const subsMiscWithNotesRows: SubsMiscWithNotesRow = [];
      const subsMiscSum: ISubsMiscSum = { subcontractorCost: CONSTANTS.ZERO, miscellaneousCost: CONSTANTS.ZERO };
      const subsMiscWithNotesSum: ISubsMiscSum = { subcontractorCost: CONSTANTS.ZERO, miscellaneousCost: CONSTANTS.ZERO };

      // Add area's own subcontractors and miscellaneous for all reports
      const areaRows1 = this.collectSubsAndMiscRows(areaMiscData, subsMiscSum);
      const areaRows2 = this.collectSubsAndMiscRowsWithNotes(areaMiscData, subsMiscWithNotesSum);
      subsMiscRows.push(...areaRows1);
      subsMiscWithNotesRows.push(...areaRows2);

      const childAreas = this.processAreaChildrenForAllReports(area, subsMiscRows, subsMiscWithNotesRows, subsMiscSum, subsMiscWithNotesSum);

      // Create table for Subs and Mics Report if there are rows
      const areaLabel = this.translate.instant("AREA_LEVEL") + CONSTANTS.COLON + CONSTANTS.SPACE;
      if (subsMiscRows.length) {
        this.generateSubsMiscTable(areaLabel + area.itemName);
        const tableIndex = this.subContractorMiscInfoList.length - CONSTANTS.ONE;
        this.addDataRowsToTable(tableIndex, subsMiscRows);
        this.addSubsMiscFooter(tableIndex, subsMiscSum);
      }

      // Create table for Subs and Mics with Notes Report if there are rows
      if (subsMiscWithNotesRows.length) {
        this.generateSubsMiscTableWithNotes(areaLabel + area.itemName);
        const tableIndex = this.subContractorMiscWithNotesInfoList.length - CONSTANTS.ONE;
        this.addDataRowsToTableWithNotes(tableIndex, subsMiscWithNotesRows);
        this.addSubsMiscFooterWithNotes(tableIndex, subsMiscWithNotesSum);
      }

      for (const childArea of childAreas) {
        this.processAreaLevelAllReports(childArea);
      }
    }

    private processAreaChildrenForAllReports(
      area: SQWebArea,
      subsMiscRows: SubsMiscRow,
      subsMiscWithNotesRows: SubsMiscWithNotesRow,
      subsMiscSum: ISubsMiscSum,
      subsMiscWithNotesSum: ISubsMiscSum
    ): SQWebArea[] {
      const areaUniqueId = area.itemUniqueId;
      const typicalOfHelpers = this.commonDataService.getTypicalOfHelpers();
      const childAreas: SQWebArea[] = [];

      for (const item of area.children) {
        const anyItem = item as SQWebAreaChildren;

        if (!anyItem?.isItemVisible) {
          continue;
        }

        if (anyItem.nodeType === CONSTANTS.NODETYPE_BSETSYSTEM) {
          const system = item as SQWebSystem;

          // Typical-of logic
          const typicalId = system.systemTypicalOfHelperUniqueId;
          let miscDataId = system.itemUniqueId;
          let typicalOf = '';
          let typicalOfCountInArea = 1;

          if (typicalId) {
            // Skip duplicate typical systems in this area
            if (this.shouldSkipSystem(system, areaUniqueId)) {
              continue;
            }

            const helper: TypicalOfSystemHelper = typicalOfHelpers[typicalId];
            const totalCount = helper.typicalOfSystems?.length ?? CONSTANTS.ONE;
            const translatedInArea = this.translate.instant("IN_AREA");
            // Derive per-area count from helper's typicalOfSystems using parentUniqueId
            typicalOfCountInArea = helper.typicalOfSystems.filter(s => s.parentUniqueId === areaUniqueId).length ?? CONSTANTS.ONE;
            typicalOf = `${totalCount} [ ${typicalOfCountInArea} ${translatedInArea} ]`;

            // Use master's ID to fetch misc data
            if (helper?.typicalOfCollectionHolderId) {
              miscDataId = helper.typicalOfCollectionHolderId;
            }
          }

          const systemMiscData = this.miscLevelwiseData.miscsCollection[miscDataId];
          
          // Collect system rows with separate sums for multiplication
          const systemSum1: ISubsMiscSum = { 
            subcontractorCost: CONSTANTS.ZERO, 
            miscellaneousCost: CONSTANTS.ZERO 
          };
          const systemRows1 = this.collectSubsAndMiscRows(systemMiscData, systemSum1);
          const systemSum2: ISubsMiscSum = { 
            subcontractorCost: CONSTANTS.ZERO, 
            miscellaneousCost: CONSTANTS.ZERO 
          };
          const systemRows2 = this.collectSubsAndMiscRowsWithNotes(systemMiscData, systemSum2);

          // Multiply costs by typicalOfCountInArea for typical systems
          if (typicalOfCountInArea > 1) {
            for (const row of systemRows1) {
              row.subCost *= typicalOfCountInArea;
              row.miscCost *= typicalOfCountInArea;
            }
            systemSum1.subcontractorCost *= typicalOfCountInArea;
            systemSum1.miscellaneousCost *= typicalOfCountInArea;

            for (const row of systemRows2) {
              row.subCost *= typicalOfCountInArea;
              row.miscCost *= typicalOfCountInArea;
            }
            systemSum2.subcontractorCost *= typicalOfCountInArea;
            systemSum2.miscellaneousCost *= typicalOfCountInArea;
          }

          // Accumulate multiplied sums into area totals
          subsMiscSum.subcontractorCost += systemSum1.subcontractorCost;
          subsMiscSum.miscellaneousCost += systemSum1.miscellaneousCost;
          subsMiscWithNotesSum.subcontractorCost += systemSum2.subcontractorCost;
          subsMiscWithNotesSum.miscellaneousCost += systemSum2.miscellaneousCost;

          // Add to Subs and Misc Report
          if (systemRows1.length) {
            subsMiscRows.push({
              referenceNumber: this.translate.instant('SYSTEM') + CONSTANTS.COLON + CONSTANTS.SPACE + system.itemName,
              description: '',
              subCost: CONSTANTS.ZERO,
              miscCost: CONSTANTS.ZERO,
              isSystemHeader: true,
              typicalOf: typicalOf
            });
            subsMiscRows.push(...systemRows1);
          }

          // Add to Subs and Misc with Notes Report
          if (systemRows2.length) {
            subsMiscWithNotesRows.push({
              referenceNumber: '',
              description: this.translate.instant('SYSTEM') + CONSTANTS.COLON + CONSTANTS.SPACE + system.itemName,
              subCost: CONSTANTS.ZERO,
              miscCost: CONSTANTS.ZERO,
              notes: '',
              isSystemHeader: true,
              typicalOf: typicalOf
            });
            subsMiscWithNotesRows.push(...systemRows2);
          }
        } else if (anyItem.nodeType === CONSTANTS.NODETYPE_AREA) {
          childAreas.push(item as SQWebArea);
        }
      }

      return childAreas;
    }

    private generateSubsMiscTable(levelName: string, itemName?: string): void {
      this.subContractorMiscInfoList.push({
        tableHeader: this.reportsCommonService.generateReportTableHeader(levelName, itemName),
        title: this.generateSubsMiscTitleHeaderRow(),
        data: [],
        tableFooter: [],
        isPageBreakAfter: false,
        isTableBordered: true
      });
    }

    private generateSubsMiscTitleHeaderRow(): IDataItem[] {
      return [
        { data: this.translate.instant('REFERENCE')+ CONSTANTS.SPACE + CONSTANTS.HASH, isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center", width: "200" },
        { data: this.translate.instant('TYPICAL_OF'), isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
        { data: this.translate.instant('SUBCONTRACTORS'), isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
        { data: this.translate.instant('MISCELLANEOUS'), isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" }
      ];
    }

    private addSubsMiscFooter(tableIndex: number, sum: ISubsMiscSum): void {
      this.subContractorMiscInfoList[tableIndex].tableFooter.push({
        headers: [
          { headerText: this.translate.instant('TOTALS'), columnspan: CONSTANTS.ONE, alignment: "left" },
          { headerText: '', columnspan: CONSTANTS.ONE, alignment: "center" },
          { headerText: sum.subcontractorCost > CONSTANTS.ZERO ? this.formatCurrency(sum.subcontractorCost) : '', columnspan: CONSTANTS.ONE, alignment: "right" },
          { headerText: sum.miscellaneousCost > CONSTANTS.ZERO ? this.formatCurrency(sum.miscellaneousCost) : '', columnspan: CONSTANTS.ONE, alignment: "right" }
        ]
      });
    }

    private generateSubsMiscTableWithNotes(levelName: string, itemName?: string): void {
      this.subContractorMiscWithNotesInfoList.push({
        tableHeader: this.reportsCommonService.generateReportTableHeader(levelName, itemName),
        title: this.generateSubsMiscWithNotesTitleHeaderRow(),
        data: [],
        tableFooter: [],
        isPageBreakAfter: false,
        isTableBordered: true
      });
    }

    private generateSubsMiscWithNotesTitleHeaderRow(): IDataItem[] {
      return [
        { data: this.translate.instant('REFERENCE') + CONSTANTS.SPACE + CONSTANTS.HASH, isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center", width: "70" },
        { data: this.translate.instant('DESCRIPTION'), isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center", width: "180" },
        { data: this.translate.instant('TYPICAL_OF'), isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
        { data: this.translate.instant('SUBCONTRACTORS'), isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
        { data: this.translate.instant('MISCELLANEOUS'), isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
        { data: this.translate.instant('NOTES'), isSubHeader: false, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center", width: "180" }
      ];
    }

    private addSubsMiscFooterWithNotes(tableIndex: number, sum: ISubsMiscSum): void {
      this.subContractorMiscWithNotesInfoList[tableIndex].tableFooter.push({
        headers: [
          { headerText: this.translate.instant('TOTALS'), columnspan: CONSTANTS.ONE, alignment: "left" },
          { headerText: '', columnspan: CONSTANTS.ONE, alignment: "left" },
          { headerText: '', columnspan: CONSTANTS.ONE, alignment: "center" },
          { headerText: sum.subcontractorCost > CONSTANTS.ZERO ? this.formatCurrency(sum.subcontractorCost) : '', columnspan: CONSTANTS.ONE, alignment: "right" },
          { headerText: sum.miscellaneousCost > CONSTANTS.ZERO ? this.formatCurrency(sum.miscellaneousCost) : '', columnspan: CONSTANTS.ONE, alignment: "right" },
          { headerText: '', columnspan: CONSTANTS.ONE, alignment: "left" }
        ]
      });
    } 
    private formatCurrency(value: number): string {
    return this.multilingualCurrencyPipe.transform(value);
  }

}
